import React from 'react';
import { AdminSettings } from '../../types';
import { adminService } from '../../services/adminService';
import { InboundBridgeReport, inboundInboxService } from '../../services/inboundInboxService';

type BridgeHealth = {
  lastIngestAt: string | null;
  ageMinutes: number | null;
  status: 'OK' | 'STALE' | 'DEAD' | 'UNKNOWN';
};

type Props = {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
  saveSettings: (s: AdminSettings) => Promise<void>;
};

export const InboundBridgeTab: React.FC<Props> = ({ settings, setSettings, saveSettings }) => {
  const toDatetimeLocal = React.useCallback((date: Date) => {
    const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 16);
  }, []);

  // ── Bridge health ──────────────────────────────────────────────────────────
  const [health, setHealth] = React.useState<BridgeHealth | null>(null);
  const [healthLoading, setHealthLoading] = React.useState(false);

  const fetchHealth = React.useCallback(async () => {
    setHealthLoading(true);
    try {
      const stats = await inboundInboxService.getQueueStats({});
      setHealth(stats.bridgeHealth);
    } catch {
      // leave null
    } finally {
      setHealthLoading(false);
    }
  }, []);

  React.useEffect(() => { void fetchHealth(); }, [fetchHealth]);
  React.useEffect(() => {
    const timer = setInterval(() => { void fetchHealth(); }, 30000);
    return () => clearInterval(timer);
  }, [fetchHealth]);

  // ── Bridge config draft ───────────────────────────────────────────────────
  const bridge = settings?.emailSystem?.inboundBridge;
  const [enabled, setEnabled]                       = React.useState(bridge?.enabled ?? false);
  const [ingestSecret, setIngestSecret]             = React.useState(bridge?.ingestSecret ?? '');
  const [mailboxAddress, setMailboxAddress]         = React.useState(bridge?.mailboxAddress ?? 'fly@miracleair.tz');
  const [backfillWebhookUrl, setBackfillWebhookUrl] = React.useState(bridge?.backfillWebhookUrl ?? '');
  const [backfillAuthHeaderName, setBackfillAuthHeaderName]     = React.useState(bridge?.backfillAuthHeaderName ?? '');
  const [backfillAuthHeaderSecret, setBackfillAuthHeaderSecret] = React.useState(bridge?.backfillAuthHeaderSecret ?? '');
  const [automationEnabled, setAutomationEnabled] = React.useState(bridge?.automation?.enabled !== false);
  const [autoBackfillOnRecovery, setAutoBackfillOnRecovery] = React.useState(bridge?.automation?.autoBackfillOnRecovery !== false);
  const [staleAfterMinutes, setStaleAfterMinutes] = React.useState(Number(bridge?.automation?.staleAfterMinutes || 120));
  const [deadAfterMinutes, setDeadAfterMinutes] = React.useState(Number(bridge?.automation?.deadAfterMinutes || 360));
  const [autoBackfillCooldownMinutes, setAutoBackfillCooldownMinutes] = React.useState(Number(bridge?.automation?.autoBackfillCooldownMinutes || 30));
  const [configSaving, setConfigSaving] = React.useState(false);
  const [configResult, setConfigResult] = React.useState('');
  const [showSecret, setShowSecret] = React.useState(false);

  // ── Watched senders (CEO / VIP emails) ────────────────────────────────────
  const [watchedSenders, setWatchedSenders] = React.useState<string[]>(
    () => (settings.emailSystem?.intake?.watchedSenders ?? []).map(String)
  );
  const [watchedInput, setWatchedInput] = React.useState('');
  const [watchedSaving, setWatchedSaving] = React.useState(false);
  const [watchedResult, setWatchedResult] = React.useState('');

  const addWatched = () => {
    const email = watchedInput.trim().toLowerCase();
    if (!email || watchedSenders.includes(email)) { setWatchedInput(''); return; }
    setWatchedSenders(prev => [...prev, email]);
    setWatchedInput('');
  };

  const removeWatched = (email: string) => setWatchedSenders(prev => prev.filter(e => e !== email));

  const saveWatched = async () => {
    setWatchedSaving(true);
    setWatchedResult('');
    try {
      const next: AdminSettings = {
        ...settings,
        emailSystem: {
          ...settings.emailSystem,
          intake: {
            ...(settings.emailSystem?.intake || ({} as any)),
            watchedSenders,
          },
        },
      };
      await saveSettings(next);
      setSettings(next);
      setWatchedResult('Watched senders saved.');
    } catch (err: any) {
      setWatchedResult(`Error: ${err?.message || 'Failed to save'}`);
    } finally {
      setWatchedSaving(false);
    }
  };

  // ── Sender allowlist (own-domain exceptions) ──────────────────────────────
  const [senderAllowlist, setSenderAllowlist] = React.useState<string[]>(
    () => (settings.emailSystem?.intake?.senderAllowlist ?? []).map(String)
  );
  const [allowlistInput, setAllowlistInput] = React.useState('');
  const [allowlistSaving, setAllowlistSaving] = React.useState(false);
  const [allowlistResult, setAllowlistResult] = React.useState('');

  const addAllowlist = () => {
    const email = allowlistInput.trim().toLowerCase();
    if (!email || senderAllowlist.includes(email)) { setAllowlistInput(''); return; }
    setSenderAllowlist(prev => [...prev, email]);
    setAllowlistInput('');
  };

  const removeAllowlist = (email: string) => setSenderAllowlist(prev => prev.filter(e => e !== email));

  const saveAllowlist = async () => {
    setAllowlistSaving(true);
    setAllowlistResult('');
    try {
      const next: AdminSettings = {
        ...settings,
        emailSystem: {
          ...settings.emailSystem,
          intake: {
            ...(settings.emailSystem?.intake || ({} as any)),
            senderAllowlist,
          },
        },
      };
      await saveSettings(next);
      setSettings(next);
      setAllowlistResult('Sender allowlist saved.');
    } catch (err: any) {
      setAllowlistResult(`Error: ${err?.message || 'Failed to save'}`);
    } finally {
      setAllowlistSaving(false);
    }
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    setConfigResult('');
    try {
      const next: AdminSettings = {
        ...settings,
        emailSystem: {
          ...settings.emailSystem,
          inboundBridge: {
            ...(settings.emailSystem?.inboundBridge || {}),
            enabled,
            ingestSecret: ingestSecret === '***' ? (bridge?.ingestSecret ?? '') : ingestSecret,
            mailboxAddress,
            allowEnvFallback: bridge?.allowEnvFallback ?? true,
            backfillWebhookUrl,
            backfillAuthHeaderName,
            backfillAuthHeaderSecret,
            automation: {
              enabled: automationEnabled,
              autoBackfillOnRecovery,
              staleAfterMinutes: Math.max(5, Number(staleAfterMinutes || 120)),
              deadAfterMinutes: Math.max(Math.max(5, Number(staleAfterMinutes || 120)) + 1, Number(deadAfterMinutes || 360)),
              autoBackfillCooldownMinutes: Math.max(1, Number(autoBackfillCooldownMinutes || 30)),
            },
          },
        },
      };
      await saveSettings(next);
      setSettings(next);
      setConfigResult('Settings saved.');
    } catch (err: any) {
      setConfigResult(`Error: ${err?.message || 'Failed to save'}`);
    } finally {
      setConfigSaving(false);
    }
  };

  // ── Backfill ───────────────────────────────────────────────────────────────
  const [bfFrom, setBfFrom]     = React.useState('');
  const [bfTo, setBfTo]         = React.useState('');
  const [bfInboxKey, setBfInboxKey] = React.useState('fly');
  const [bfExactSubject, setBfExactSubject] = React.useState('');
  const [bfLoading, setBfLoading]   = React.useState(false);
  const [bfResult, setBfResult]     = React.useState('');

  const [singleMailboxKey, setSingleMailboxKey] = React.useState('miracle');
  const [singleGmailMessageId, setSingleGmailMessageId] = React.useState('');
  const [singleLoading, setSingleLoading] = React.useState(false);
  const [singleResult, setSingleResult] = React.useState('');

  const triggerBackfill = async () => {
    if (!bfFrom || !bfTo) { setBfResult('Both dates are required.'); return; }
    setBfLoading(true); setBfResult('');
    try {
      await inboundInboxService.triggerBackfill({
        fromDate: bfFrom,
        toDate: bfTo,
        inboxKey: bfInboxKey,
        exactSubject: bfExactSubject.trim() || undefined,
      });
      setBfResult('Backfill triggered. H.BRD will re-ingest emails for the selected period. Duplicates are automatically skipped.');
    } catch (err: any) {
      setBfResult(`Error: ${err?.message || 'Failed to trigger backfill'}`);
    } finally {
      setBfLoading(false);
    }
  };

  const importSingleMessage = async () => {
    const gmailMessageId = singleGmailMessageId.trim();
    if (!gmailMessageId) {
      setSingleResult('Gmail message id is required.');
      return;
    }
    setSingleLoading(true);
    setSingleResult('');
    try {
      const result = await inboundInboxService.importGmailMessage({
        mailboxKey: singleMailboxKey.trim() || undefined,
        gmailMessageId,
      });
      if (result.skipped) {
        setSingleResult(`Skipped: ${result.reason || 'message was intentionally skipped by inbound rules'}.`);
      } else if (result.deduped) {
        setSingleResult(`Already in ME-OS. Thread ${result.thread?.id || 'found'} / message ${result.message?.id || 'found'}.`);
      } else {
        setSingleResult(`Imported successfully. Thread ${result.thread?.id || 'created'} / message ${result.message?.id || 'created'}.`);
      }
    } catch (err: any) {
      setSingleResult(`Error: ${err?.message || 'Failed to import Gmail message'}`);
    } finally {
      setSingleLoading(false);
    }
  };

  // ── Report ─────────────────────────────────────────────────────────────────
  const [rpFrom, setRpFrom] = React.useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return toDatetimeLocal(d);
  });
  const [rpTo, setRpTo] = React.useState(() => toDatetimeLocal(new Date()));
  const [rpInboxKey, setRpInboxKey] = React.useState('fly');
  const [rpLoading, setRpLoading] = React.useState(false);
  const [rpError, setRpError] = React.useState('');
  const [report, setReport] = React.useState<InboundBridgeReport | null>(null);

  const fetchReport = React.useCallback(async () => {
    setRpLoading(true);
    setRpError('');
    try {
      const data = await inboundInboxService.getBridgeReport({
        fromDate: rpFrom ? new Date(rpFrom).toISOString() : undefined,
        toDate: rpTo ? new Date(rpTo).toISOString() : undefined,
        inboxKey: rpInboxKey || undefined,
      });
      setReport(data);
    } catch (err: any) {
      setRpError(err?.message || 'Failed to load bridge report');
    } finally {
      setRpLoading(false);
    }
  }, [rpFrom, rpTo, rpInboxKey]);

  React.useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  // ── Health helpers ─────────────────────────────────────────────────────────
  const ageLabel = !health ? '—' :
    health.ageMinutes === null ? 'never synced' :
    health.ageMinutes < 60 ? `${health.ageMinutes}m ago` :
    health.ageMinutes < 1440 ? `${Math.floor(health.ageMinutes / 60)}h ago` :
    `${Math.floor(health.ageMinutes / 1440)}d ago`;

  const lastIngestLabel = !health?.lastIngestAt ? 'Never' :
    new Date(health.lastIngestAt).toLocaleString();

  type StatusKey = 'OK' | 'STALE' | 'DEAD' | 'UNKNOWN';
  const STATUS_STYLE: Record<StatusKey, { dot: string; badge: string; label: string }> = {
    OK:      { dot: 'bg-emerald-400',                   badge: 'border-emerald-200 bg-emerald-50 text-emerald-700', label: 'Bridge OK' },
    STALE:   { dot: 'bg-amber-400 animate-pulse',       badge: 'border-amber-200  bg-amber-50  text-amber-700',  label: 'Bridge Stale' },
    DEAD:    { dot: 'bg-rose-500 animate-pulse',        badge: 'border-rose-200   bg-rose-50   text-rose-700',   label: 'Bridge Dead' },
    UNKNOWN: { dot: 'bg-slate-300',                     badge: 'border-slate-200  bg-slate-50  text-slate-500',  label: 'Unknown' },
  };
  const s = STATUS_STYLE[(health?.status as StatusKey) || 'UNKNOWN'];

  return (
    <div className="space-y-6">

      {/* ── 1. Bridge Health ─────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Bridge Health</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Last time H.BRD successfully delivered an email to MA-OS.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void fetchHealth()}
            disabled={healthLoading}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-700 hover:border-slate-300 transition-all disabled:opacity-50"
          >
            {healthLoading ? 'Checking…' : 'Refresh'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {/* Status badge */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Status</div>
            {health ? (
              <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-black tracking-[0.06em] ${s.badge}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                {s.label}
              </div>
            ) : (
              <div className="mt-2 text-sm text-slate-400">—</div>
            )}
          </div>

          {/* Last ingest */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Last Ingest</div>
            <div className="mt-2 text-sm font-semibold text-slate-700">{lastIngestLabel}</div>
          </div>

          {/* Age */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Age</div>
            <div className="mt-2 text-sm font-semibold text-slate-700">{ageLabel}</div>
          </div>
        </div>

        {health && health.status !== 'OK' && (
          <div className={`rounded-xl border px-4 py-3 text-xs font-medium ${health.status === 'DEAD' ? 'border-rose-200 bg-rose-50 text-rose-700' : health.status === 'STALE' ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
            {health.status === 'DEAD'
              ? 'Bridge has not delivered any emails in more than 6 hours. Use the Backfill section below to recover missed emails after restoring the bridge.'
              : health.status === 'STALE'
              ? 'Bridge has not delivered emails in the last 2–6 hours. Monitor and consider running a backfill if emails are missing.'
              : 'Bridge has never delivered an email, or status is unknown. Configure the bridge and verify H.BRD is running.'}
          </div>
        )}
      </div>

      {/* ── 2. Bridge Configuration ──────────────────────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Bridge Configuration</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Settings for the H.BRD → MA-OS inbound email bridge.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void saveConfig()}
            disabled={configSaving}
            className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-slate-800 transition-all disabled:opacity-50"
          >
            {configSaving ? 'Saving…' : 'Save'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Bridge Enabled</span>
            <select
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
              value={enabled ? 'YES' : 'NO'}
              onChange={e => setEnabled(e.target.value === 'YES')}
            >
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Mailbox Address</span>
            <input
              type="text"
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 placeholder-slate-400 outline-none focus:border-slate-400"
              value={mailboxAddress}
              onChange={e => setMailboxAddress(e.target.value)}
              placeholder="fly@miracleair.tz"
            />
          </label>

          <label className="flex flex-col gap-1.5 md:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Ingest Secret</span>
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-24 text-sm font-semibold text-slate-700 placeholder-slate-400 outline-none focus:border-slate-400"
                value={ingestSecret}
                onChange={e => setIngestSecret(e.target.value)}
                placeholder="Shared secret (leave as *** to keep existing)"
              />
              <button
                type="button"
                onClick={() => setShowSecret(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg border border-slate-200 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 hover:border-slate-300 transition-all"
              >
                {showSecret ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          <label className="flex flex-col gap-1.5 md:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Backfill Webhook URL</span>
            <input
              type="text"
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 placeholder-slate-400 outline-none focus:border-slate-400"
              value={backfillWebhookUrl}
              onChange={e => setBackfillWebhookUrl(e.target.value)}
              placeholder="https://H.BRD.miracleair.com/webhook/email-backfill"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Backfill Auth Header Name</span>
            <input
              type="text"
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 placeholder-slate-400 outline-none focus:border-slate-400"
              value={backfillAuthHeaderName}
              onChange={e => setBackfillAuthHeaderName(e.target.value)}
              placeholder="X-Webhook-Secret"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Backfill Auth Header Value</span>
            <input
              type="password"
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 placeholder-slate-400 outline-none focus:border-slate-400"
              value={backfillAuthHeaderSecret}
              onChange={e => setBackfillAuthHeaderSecret(e.target.value)}
              placeholder="secret value"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Automation Enabled</span>
            <select
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
              value={automationEnabled ? 'YES' : 'NO'}
              onChange={e => setAutomationEnabled(e.target.value === 'YES')}
            >
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Auto Backfill On Recovery</span>
            <select
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
              value={autoBackfillOnRecovery ? 'YES' : 'NO'}
              onChange={e => setAutoBackfillOnRecovery(e.target.value === 'YES')}
            >
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Stale After (minutes)</span>
            <input
              type="number"
              min={5}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 placeholder-slate-400 outline-none focus:border-slate-400"
              value={staleAfterMinutes}
              onChange={e => setStaleAfterMinutes(Number(e.target.value || 120))}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Dead After (minutes)</span>
            <input
              type="number"
              min={6}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 placeholder-slate-400 outline-none focus:border-slate-400"
              value={deadAfterMinutes}
              onChange={e => setDeadAfterMinutes(Number(e.target.value || 360))}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Auto Backfill Cooldown (minutes)</span>
            <input
              type="number"
              min={1}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 placeholder-slate-400 outline-none focus:border-slate-400"
              value={autoBackfillCooldownMinutes}
              onChange={e => setAutoBackfillCooldownMinutes(Number(e.target.value || 30))}
            />
          </label>
        </div>

        {configResult && (
          <div className={`rounded-xl border px-3 py-2 text-xs font-medium ${configResult.startsWith('Error') ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
            {configResult}
          </div>
        )}
      </div>

      {/* ── 3. Watched Senders (CEO / VIP) ───────────────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Watched Senders — CEO / VIP Badge</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Emails from these addresses get a gold ★ and a <span className="font-semibold text-amber-700">C.E.O</span> badge in the queue so operators prioritise them immediately.
          </p>
        </div>
        {/* Current list */}
        <div className="flex flex-wrap gap-2">
          {watchedSenders.length === 0 && (
            <span className="text-xs text-slate-400 italic">No watched senders configured.</span>
          )}
          {watchedSenders.map(email => (
            <span
              key={email}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 py-0.5 pl-3 pr-1.5 text-[12px] font-medium text-amber-800"
            >
              {email}
              <button
                type="button"
                onClick={() => removeWatched(email)}
                className="flex h-4 w-4 items-center justify-center rounded-full text-amber-600 hover:bg-amber-200"
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        {/* Add new */}
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="ceo@example.com"
            value={watchedInput}
            onChange={e => setWatchedInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addWatched(); } }}
            className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={addWatched}
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-700 hover:border-slate-300 transition-all"
          >
            Add
          </button>
        </div>
        {/* Save */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void saveWatched()}
            disabled={watchedSaving}
            className="rounded-xl bg-slate-900 px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white transition hover:bg-slate-700 disabled:opacity-50"
          >
            {watchedSaving ? 'Saving…' : 'Save Watched Senders'}
          </button>
          {watchedResult && (
            <span className={`text-xs font-medium ${watchedResult.startsWith('Error') ? 'text-rose-600' : 'text-emerald-600'}`}>
              {watchedResult}
            </span>
          )}
        </div>
      </div>

      {/* ── 5. Sender Allowlist (own-domain exceptions) ──────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Own-Domain Sender Allowlist</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Emails sent from Miracle Air domains (@miracleair.tz etc.) are hidden from the queue to prevent internal noise.
            Add specific addresses here to allow them through (e.g. a shared booking address that also emails the inbox).
          </p>
        </div>
        <div className="flex flex-wrap gap-2 min-h-[32px]">
          {senderAllowlist.length === 0 && (
            <span className="text-xs text-slate-400 italic">No exceptions configured</span>
          )}
          {senderAllowlist.map(email => (
            <span key={email} className="flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs font-medium text-blue-800">
              {email}
              <button type="button" onClick={() => removeAllowlist(email)} className="text-blue-400 hover:text-blue-700 leading-none">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="booking@miracleair.tz"
            value={allowlistInput}
            onChange={e => setAllowlistInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAllowlist())}
            className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
          <button type="button" onClick={addAllowlist} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100">
            Add
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void saveAllowlist()}
            disabled={allowlistSaving}
            className="rounded-xl bg-slate-900 px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white transition hover:bg-slate-700 disabled:opacity-50"
          >
            {allowlistSaving ? 'Saving…' : 'Save Allowlist'}
          </button>
          {allowlistResult && (
            <span className={`text-xs font-medium ${allowlistResult.startsWith('Error') ? 'text-rose-600' : 'text-emerald-600'}`}>
              {allowlistResult}
            </span>
          )}
        </div>
      </div>

      {/* ── 6. Single Gmail Message Import ───────────────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Import Single Gmail Message</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Recover one missing Gmail email directly into ME-OS when you already know the Gmail message id.
            This uses the normal inbound ingest pipeline and safely skips duplicates.
          </p>
          <p className="mt-2 text-[11px] text-slate-400">
            Admin-only tip: open the email in Gmail and copy the long id from the URL segment after <span className="font-semibold text-slate-500">/all/</span> or <span className="font-semibold text-slate-500">#inbox/</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Mailbox Key</span>
            <input
              type="text"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
              value={singleMailboxKey}
              onChange={e => setSingleMailboxKey(e.target.value)}
              placeholder="miracle"
            />
          </label>

          <label className="flex flex-col gap-1.5 md:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Gmail Message ID</span>
            <input
              type="text"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
              value={singleGmailMessageId}
              onChange={e => setSingleGmailMessageId(e.target.value)}
              placeholder="19efddea0483f154"
            />
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void importSingleMessage()}
            disabled={singleLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.14em] text-white shadow-sm hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {singleLoading ? (
              <><span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" /> Importing…</>
            ) : (
              'Import Gmail Message'
            )}
          </button>
          <span className="text-[11px] text-slate-400">Use this for one missing thread before triggering a wider date-range backfill.</span>
        </div>

        {singleResult && (
          <div className={`rounded-xl border px-3 py-2.5 text-xs font-medium ${singleResult.startsWith('Error') || singleResult.startsWith('Skipped') ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
            {singleResult}
          </div>
        )}
      </div>

      {/* ── 7. Backfill Trigger ───────────────────────────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Backfill Emails</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Select the period when the bridge was down. MA-OS instructs H.BRD to re-fetch all Gmail messages
            from that window and re-ingest them. Emails already stored are automatically skipped.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Gap Start</span>
            <input
              type="datetime-local"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
              value={bfFrom}
              onChange={e => setBfFrom(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Gap End</span>
            <input
              type="datetime-local"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
              value={bfTo}
              onChange={e => setBfTo(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Inbox Key</span>
            <input
              type="text"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
              value={bfInboxKey}
              onChange={e => setBfInboxKey(e.target.value)}
              placeholder="fly"
            />
          </label>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Exact Subject (optional)</span>
          <input
            type="text"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
            value={bfExactSubject}
            onChange={e => setBfExactSubject(e.target.value)}
            placeholder="Leave empty to search all emails in this period"
          />
        </label>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void triggerBackfill()}
            disabled={bfLoading || !backfillWebhookUrl}
            title={!backfillWebhookUrl ? 'Configure Backfill Webhook URL above first' : undefined}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.14em] text-white shadow-sm hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {bfLoading ? (
              <><span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" /> Triggering…</>
            ) : (
              <><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>Trigger Backfill</>
            )}
          </button>
          {!backfillWebhookUrl && (
            <span className="text-[11px] text-slate-400">Set the Backfill Webhook URL in configuration above first.</span>
          )}
        </div>

        {bfResult && (
          <div className={`rounded-xl border px-3 py-2.5 text-xs font-medium ${bfResult.startsWith('Error') ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
            {bfResult}
          </div>
        )}
      </div>

      {/* ── 8. Bridge Report ─────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Bridge Report</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Date-range report for outages, automatic recovery, and estimated missed volume.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void fetchReport()}
            disabled={rpLoading}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-700 hover:border-slate-300 transition-all disabled:opacity-50"
          >
            {rpLoading ? 'Loading…' : 'Generate'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">From</span>
            <input
              type="datetime-local"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
              value={rpFrom}
              onChange={e => setRpFrom(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">To</span>
            <input
              type="datetime-local"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
              value={rpTo}
              onChange={e => setRpTo(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Inbox Key</span>
            <input
              type="text"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-slate-400"
              value={rpInboxKey}
              onChange={e => setRpInboxKey(e.target.value)}
              placeholder="fly"
            />
          </label>
        </div>

        {rpError && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs font-medium text-rose-700">
            {rpError}
          </div>
        )}

        {report && (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-8">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Inbound Total</div>
                <div className="mt-2 text-lg font-black text-slate-900">{report.summary.totalInbound}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Outage Windows</div>
                <div className="mt-2 text-lg font-black text-slate-900">{report.summary.outageWindows}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Outage Minutes</div>
                <div className="mt-2 text-lg font-black text-slate-900">{report.summary.outageMinutes}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">In Outage Windows</div>
                <div className="mt-2 text-lg font-black text-slate-900">{report.summary.messagesInOutageWindows}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Auto Backfills</div>
                <div className="mt-2 text-lg font-black text-slate-900">{report.summary.autoBackfillTriggered}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Auto Failed</div>
                <div className="mt-2 text-lg font-black text-rose-700">{report.summary.autoBackfillFailed}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Manual Backfills</div>
                <div className="mt-2 text-lg font-black text-slate-900">{report.summary.manualBackfillTriggered}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Manual Failed</div>
                <div className="mt-2 text-lg font-black text-rose-700">{report.summary.manualBackfillFailed}</div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-200 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                Backfill Runs (Latest 30)
              </div>
              {report.autoBackfillRuns.length === 0 ? (
                <div className="px-4 py-4 text-sm text-slate-500">No runs in selected period.</div>
              ) : (
                <div className="max-h-72 overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Triggered</th>
                        <th className="px-3 py-2 text-left font-semibold">Source</th>
                        <th className="px-3 py-2 text-left font-semibold">Inbox</th>
                        <th className="px-3 py-2 text-left font-semibold">Status</th>
                        <th className="px-3 py-2 text-right font-semibold">Found</th>
                        <th className="px-3 py-2 text-right font-semibold">Inserted</th>
                        <th className="px-3 py-2 text-right font-semibold">Deduped</th>
                        <th className="px-3 py-2 text-right font-semibold">Failed</th>
                        <th className="px-3 py-2 text-left font-semibold">Subject</th>
                        <th className="px-3 py-2 text-left font-semibold">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...report.autoBackfillRuns]
                        .sort((a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime())
                        .map((row, idx) => (
                          <tr key={`${row.triggeredAt}-${row.inboxKey}-${idx}`} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-700">{row.triggeredAt ? new Date(row.triggeredAt).toLocaleString() : '—'}</td>
                            <td className="px-3 py-2 text-slate-700">{row.source || 'AUTO'}</td>
                            <td className="px-3 py-2 text-slate-700">{row.inboxKey || '—'}</td>
                            <td className={`px-3 py-2 font-semibold ${row.status === 'FAILED' ? 'text-rose-700' : row.status === 'SUCCESS' ? 'text-emerald-700' : 'text-slate-700'}`}>{row.status || 'UNKNOWN'}</td>
                            <td className="px-3 py-2 text-right text-slate-700">{row.found ?? '—'}</td>
                            <td className="px-3 py-2 text-right text-slate-700">{row.inserted ?? '—'}</td>
                            <td className="px-3 py-2 text-right text-slate-700">{row.deduped ?? '—'}</td>
                            <td className="px-3 py-2 text-right text-slate-700">{row.failed ?? '—'}</td>
                            <td className="px-3 py-2 text-slate-700">{row.exactSubject || '—'}</td>
                            <td className="px-3 py-2 text-rose-700">{row.error || '—'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
